import {
  type IUseGetSocialOfferPrescriptionFilledResponse,
  displayOrDownloadUrlDocument,
  useDownloadDocument,
} from "@api";
import { getSettingsStatusLabel } from "@candidate/components/utils/formatTools";
import { NoteAltOutlined, Visibility } from "@mui/icons-material";
import { Box, Chip, Divider, IconButton, Stack, type Theme, Tooltip, Typography, useMediaQuery } from "@mui/material";
import { useGlobalContext } from "@root/context";
import { useI18nContext } from "@root/utils/organisation";
import { formatPhoneNumber } from "@root/utils/phoneLib";
import { isAuthorize } from "@styleguide";
import clsx from "clsx";
import { type Dispatch, type SetStateAction, useMemo } from "react";
import { Link } from "react-router-dom";
import type { IPopoverState } from "../index";
import { PrescriptionTableRow, classes } from "../styles";
import { Accompaniement } from "./accompaniement";
import { AuthorChip } from "./authorChip";

interface IPrescriptionCandidatesCardProps {
  data: IUseGetSocialOfferPrescriptionFilledResponse;
  setAccompaniementPopoverState: Dispatch<SetStateAction<IPopoverState>>;
  completePrescription: () => void;
}

export const PrescriptionCandidatesCard = ({
  data,
  setAccompaniementPopoverState,
  completePrescription,
}: IPrescriptionCandidatesCardProps) => {
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up("desktop"));
  const { settingsV2, user } = useGlobalContext();
  const { timeZone } = useI18nContext();
  const hasVisualizeCandidatePermission = isAuthorize(user.roleV2, ["candidate.visualize"]);
  const isFullPartner = user.roleV2.value === "fullPartner" && isAuthorize(user.roleV2, ["partner.fup.complete"]);
  const [, downloadDocument] = useDownloadDocument();

  const handleDisplayDocument = async () => {
    if (data.prescription) {
      const res = await downloadDocument(data.prescription.documentId);
      if (res.status === 200) {
        displayOrDownloadUrlDocument(res.data, "display", data.prescription.name, "application/pdf");
      }
    }
  };

  const status = useMemo(() => {
    if (data.candidate.status && settingsV2.statusCandidate?.values) {
      return getSettingsStatusLabel(data.candidate.status, settingsV2.statusCandidate.values);
    }
    return "Aucun statut";
  }, [data.candidate.status, settingsV2]);
  return (
    <PrescriptionTableRow sx={{ pl: 2 }} hasVisualizeCandidatePermission={hasVisualizeCandidatePermission}>
      <Stack className={classes.root} sx={{ textDecoration: "unset", color: "unset" }}>
        {hasVisualizeCandidatePermission ? (
          <Stack
            className={clsx(classes.cell, classes.candidate)}
            component={Link}
            to={`/v2/candidate/${data.candidate._id}`}
            target="_blank"
          >
            <Typography
              variant="body1SemiBold"
              sx={{
                color: "primary.main",
                overflow: "hidden",
                textOverflow: "ellipsis",
                textDecoration: "underline",
              }}
            >
              {data.candidate.lastName} {data.candidate.firstName}
            </Typography>
          </Stack>
        ) : (
          <Stack className={clsx(classes.cell, classes.candidate)}>
            <Typography variant="body1SemiBold" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              {data.candidate.lastName} {data.candidate.firstName}
            </Typography>
          </Stack>
        )}
        <Stack className={clsx(classes.cell, classes.phone)}>
          <Typography variant="body1" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
            {formatPhoneNumber(data.candidate.phone) || "-"}
          </Typography>
        </Stack>
        {isDesktop && (
          <>
            <Stack className={clsx(classes.cell, classes.place)}>
              <Typography variant="body1" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                {data.candidate.place || "-"}
              </Typography>
            </Stack>
            <Stack className={clsx(classes.cell, classes.accompaniement)}>
              <Accompaniement
                accompaniement={data.accompaniement}
                otherIntegratedSocialOffers={data.otherIntegratedSocialOffers}
                setAccompaniementPopoverState={setAccompaniementPopoverState}
              />
            </Stack>
          </>
        )}
        <Stack className={clsx(classes.cell, classes.status)} sx={{ alignItems: "start" }}>
          <Chip
            color="primary"
            size="small"
            variant="filled"
            label={
              <Typography variant="body2SemiBold" noWrap>
                {status}
              </Typography>
            }
          />
        </Stack>
        {isDesktop && (
          <Stack className={clsx(classes.cell, classes.author)}>
            <AuthorChip author={data.author} />
          </Stack>
        )}
        <Stack className={clsx(classes.cell, classes.prescriptionDate)}>
          <Typography variant="body1" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
            {data.prescriptionDate?.toLocaleDateString("fr-FR", { timeZone }) || "-"}
          </Typography>
        </Stack>
        <Box className={clsx(classes.cell, classes.action)}>
          {data.prescription?.documentId && (
            <Tooltip title="Voir la prescription transmise">
              <span>
                <IconButton color="primary" onClick={handleDisplayDocument}>
                  <Visibility fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          <Tooltip
            title={
              !data.prescription
                ? "La prescription a été renseignée manuellement. Pour indiquer l'intégration ou le refus de la personne, le statut de l'offre doit être mis à jour dans sa fiche candidat."
                : "Compléter la prescription"
            }
          >
            <span>
              {isFullPartner && (
                <IconButton color="primary" disabled={!data.prescription} onClick={completePrescription}>
                  <NoteAltOutlined fontSize="small" />
                </IconButton>
              )}
            </span>
          </Tooltip>
        </Box>
      </Stack>
      <Divider />
    </PrescriptionTableRow>
  );
};
